import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../services/supabase';
import { SolicitacoesService } from '../services/solicitacoesService';
import { SolicitacaoAgendamento } from '../types';
import Button from '../components/common/Button';
import Input from '../components/common/Input';

interface LogUsuario {
  id: string;
  usuario_id: string;
  usuario_nome: string;
  usuario_login: string;
  acao: string;
  modulo: string;
  descricao: string;
  dados_anteriores: any;
  dados_novos: any;
  data_acao: string;
  filial_atual: string;
}

interface AcaoHistorico {
  log: LogUsuario;
  detalhes: string;
  responsavel: string;
  data: string;
  tipo: string;
}

const RastreamentoSolicitacao: React.FC = () => {
  const { user } = useAuth();
  const [solicitacaoId, setSolicitacaoId] = useState<string>('');
  const [solicitacao, setSolicitacao] = useState<SolicitacaoAgendamento | null>(null);
  const [historico, setHistorico] = useState<AcaoHistorico[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [erro, setErro] = useState<string>('');

  const buscarSolicitacao = async () => {
    if (!solicitacaoId.trim()) {
      setErro('Por favor, informe o ID da solicitação');
      return;
    }

    setLoading(true);
    setErro('');
    setSolicitacao(null);
    setHistorico([]);

    try {
      // Buscar solicitação por ID
      const solicitacaoData = await SolicitacoesService.buscarPorId(solicitacaoId);
      
      if (!solicitacaoData) {
        setErro('Solicitação não encontrada');
        return;
      }

      setSolicitacao(solicitacaoData);

      // Buscar histórico de ações (logs_usuarios)
      const { data: logs, error: logsError } = await supabase
        .from('logs_usuarios')
        .select('*')
        .eq('modulo', 'SOLICITACAO_AGENDAMENTO')
        .eq('dados_novos->>id', solicitacaoId)
        .order('data_acao', { ascending: false });

      if (logsError) {
        console.error('Erro ao buscar logs:', logsError);
      }

      // Processar logs para formato amigável
      const historicoProcessado: AcaoHistorico[] = (logs || []).map((log: LogUsuario) => {
        let detalhes = '';
        let tipo = '';

        switch (log.acao) {
          case 'CRIAR':
            tipo = 'Criação';
            detalhes = log.descricao || 'Solicitação criada';
            break;
          case 'ATUALIZAR_STATUS':
            tipo = 'Atualização de Status';
            detalhes = log.descricao || 'Status atualizado';
            break;
          case 'APROVAR':
            tipo = 'Aprovação';
            detalhes = log.descricao || 'Solicitação aprovada';
            break;
          case 'REPROVAR':
            tipo = 'Reprovação';
            detalhes = log.descricao || 'Solicitação reprovada';
            break;
          case 'EXCLUIR':
            tipo = 'Exclusão';
            detalhes = log.descricao || 'Solicitação excluída';
            break;
          default:
            tipo = 'Ação';
            detalhes = log.descricao || log.acao;
        }

        return {
          log,
          detalhes,
          responsavel: log.usuario_nome,
          data: log.data_acao,
          tipo
        };
      });

      setHistorico(historicoProcessado);

    } catch (error) {
      console.error('Erro ao buscar solicitação:', error);
      setErro('Erro ao buscar solicitação');
    } finally {
      setLoading(false);
    }
  };

  const formatarData = (data: string) => {
    return new Date(data).toLocaleString('pt-BR');
  };

  const getTipoCor = (tipo: string) => {
    switch (tipo) {
      case 'Criação':
        return 'bg-blue-100 text-blue-800';
      case 'Aprovação':
        return 'bg-green-100 text-green-800';
      case 'Reprovação':
        return 'bg-red-100 text-red-800';
      case 'Reagendamento':
        return 'bg-yellow-100 text-yellow-800';
      case 'Documento':
        return 'bg-purple-100 text-purple-800';
      case 'Comentário':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="p-6">
      <h1 className="text-3xl font-bold mb-6">🔍 Rastreamento de Solicitação</h1>

      {/* Busca por ID */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <div className="flex gap-4">
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              ID da Solicitação
            </label>
            <Input
              value={solicitacaoId}
              onChange={setSolicitacaoId}
              placeholder="Digite o ID da solicitação (ex: 89f29693-da6d-4cf4-8b20-e289f4d3b135)"
            />
          </div>
          <div className="flex items-end">
            <Button
              onClick={buscarSolicitacao}
              disabled={loading}
              className="mb-1"
            >
              {loading ? 'Buscando...' : 'Buscar'}
            </Button>
          </div>
        </div>

        {erro && (
          <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
            {erro}
          </div>
        )}
      </div>

      {/* Informações da Solicitação */}
      {solicitacao && (
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h2 className="text-xl font-bold mb-4">📋 Informações da Solicitação</h2>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <span className="text-sm font-medium text-gray-600">ID:</span>
              <p className="font-semibold text-sm">{solicitacao.id}</p>
            </div>
            <div>
              <span className="text-sm font-medium text-gray-600">Fornecedor:</span>
              <p className="font-semibold">{solicitacao.fornecedor}</p>
            </div>
            <div>
              <span className="text-sm font-medium text-gray-600">Status:</span>
              <p className="font-semibold">{solicitacao.status}</p>
            </div>
            <div>
              <span className="text-sm font-medium text-gray-600">Data Desejada:</span>
              <p className="font-semibold">{solicitacao.data_desejada || 'Não informada'}</p>
            </div>
            <div>
              <span className="text-sm font-medium text-gray-600">Peso:</span>
              <p className="font-semibold">{solicitacao.peso || 0} kg</p>
            </div>
            <div>
              <span className="text-sm font-medium text-gray-600">Volume:</span>
              <p className="font-semibold">{solicitacao.volume || 0} m³</p>
            </div>
            <div>
              <span className="text-sm font-medium text-gray-600">Criado em:</span>
              <p className="font-semibold">{formatarData(solicitacao.data_solicitacao)}</p>
            </div>
            <div>
              <span className="text-sm font-medium text-gray-600">Filial:</span>
              <p className="font-semibold">{solicitacao.filial || 'Não informada'}</p>
            </div>
          </div>
        </div>
      )}

      {/* Histórico de Ações */}
      {historico.length > 0 && (
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-bold mb-4">📜 Histórico de Ações</h2>
          
          <div className="space-y-4">
            {historico.map((acao, index) => (
              <div
                key={index}
                className="border-l-4 border-blue-500 pl-4 py-2 relative"
              >
                <div className="absolute left-0 top-2 -ml-3">
                  <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center">
                    <span className="text-white text-xs font-bold">{index + 1}</span>
                  </div>
                </div>
                
                <div className="flex justify-between items-start mb-2">
                  <div>
                    <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold ${getTipoCor(acao.tipo)}`}>
                      {acao.tipo}
                    </span>
                  </div>
                  <span className="text-sm text-gray-500">{formatarData(acao.data)}</span>
                </div>
                
                <p className="text-gray-800 font-medium mb-1">{acao.detalhes}</p>
                
                <div className="text-sm text-gray-600">
                  <span className="font-medium">Responsável:</span> {acao.responsavel}
                </div>

                {acao.log.dados_novos && (
                  <div className="mt-2 p-2 bg-gray-50 rounded text-sm text-gray-700">
                    <span className="font-medium">Detalhes:</span>
                    <pre className="mt-1 text-xs overflow-auto">{JSON.stringify(acao.log.dados_novos, null, 2)}</pre>
                  </div>
                )}

                {acao.log.filial_atual && (
                  <div className="mt-1 text-xs text-gray-400">
                    Filial: {acao.log.filial_atual}
                  </div>
                )}
              </div>
            ))}
          </div>

          {historico.length === 0 && (
            <div className="text-center py-8 text-gray-500">
              Nenhuma ação registrada para esta solicitação
            </div>
          )}
        </div>
      )}

      {solicitacao && historico.length === 0 && !loading && (
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="text-center py-8 text-gray-500">
            Nenhuma ação registrada para esta solicitação
          </div>
        </div>
      )}
    </div>
  );
};

export default RastreamentoSolicitacao;
